// eslint-disable-next-line no-restricted-imports
import * as amplitude from '@amplitude/analytics-browser';
import { AuthUser } from 'aws-amplify/auth';
import { Customer } from 'src/api/queries';

import { AMPLITUDE_API_KEY, BUILD_VERSION } from 'src/constants';
import { getUserType } from 'src/utils/getUserType';

// GDPR: initialization is deferred — Amplitude sets its device-id cookies
// only once init runs. On the public website init happens after the visitor
// accepts the cookie banner; in the app it happens on sign-in. Events
// tracked before init are queued by the SDK and flushed after it.
let amplitudeInitialized = false;

export const initAmplitude = () => {
  if (amplitudeInitialized) {
    return;
  }
  amplitudeInitialized = true;

  amplitude.init(AMPLITUDE_API_KEY, {
    appVersion: BUILD_VERSION,
    useBatch: true,
    serverZone: 'EU',
    defaultTracking: {
      attribution: true,
      fileDownloads: true,
      pageViews: true,
      sessions: true,
      formInteractions: true,
    },
  });
};

// Honors consent withdrawal: once initialized, Amplitude cannot be torn down
// within the session, but opting out stops all further collection
export const setAmplitudeOptOut = (optOut: boolean) => {
  if (!amplitudeInitialized) {
    return;
  }
  amplitude.setOptOut(optOut);
};

// Consent withdrawal cleanup: removes the identifiers Amplitude has already
// stored (AMP_* cookies, AMP_unsent_* event queue). The SDK has no API for
// this, so the storage is cleared manually by its documented key prefix.
export const clearAmplitudeStorage = () => {
  try {
    Object.keys(localStorage)
      .filter((key) => key.startsWith('AMP_'))
      .forEach((key) => localStorage.removeItem(key));
  } catch {
    // Storage unavailable — nothing to clear
  }

  // Assumes Amplitude's cookies are set with path=/ (the SDK default, not
  // configured otherwise here). Other paths cannot be enumerated from JS; if
  // a cookie is ever missed, it stays inert — with consent declined the SDK
  // never initializes, so nothing reads or refreshes it.
  const expired = 'expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';
  document.cookie.split(';').forEach((cookie) => {
    const name = cookie.split('=')[0]?.trim();
    if (name?.startsWith('AMP_')) {
      document.cookie = `${name}=; ${expired}`;
      document.cookie = `${name}=; ${expired}; domain=${location.hostname}`;
      document.cookie = `${name}=; ${expired}; domain=.${location.hostname}`;
    }
  });
};

const NON_DEF_ROLE = 'no-role-defined';

export const setAmplitudeUser = (user: AuthUser, role: string | null) => {
  // Signed-in product analytics run regardless of the public-site banner
  initAmplitude();
  amplitude.setUserId(user.username);
  const identifyObj = new amplitude.Identify();
  identifyObj.setOnce('username', user.username);
  identifyObj.set('role', role ?? NON_DEF_ROLE);
  identifyObj.setOnce('cognitoId', user.userId);

  const userType = getUserType(user);

  identifyObj.set('userType', userType);
  amplitude.identify(identifyObj);
};

export const unsetAmplitudeUser = () => {
  amplitude.reset();
};

export { amplitude };

type ButtonClickedEventProperties = {
  buttonName: string;
};

export const buttonClickedEvent = (
  eventProperties: ButtonClickedEventProperties,
) => {
  const pathname = window.location.pathname;
  amplitude.track('Button Clicked', {
    ...eventProperties,
    pathname,
  });
};

export const iconButtonClickedEvent = (
  eventProperties: ButtonClickedEventProperties,
) => {
  const pathname = window.location.pathname;
  amplitude.track('Icon Button Clicked', {
    ...eventProperties,
    pathname,
  });
};

type MenuButtonClickedEventProperties = {
  buttonName: string;
};

export const menuButtonClickedEvent = (
  eventProperties: MenuButtonClickedEventProperties,
) => {
  const pathname = window.location.pathname;
  amplitude.track('Menu Button Clicked', {
    ...eventProperties,
    pathname,
  });
};

type MenuOptionClickedEventProperties = {
  buttonName: string;
  value: string;
  label: string;
};

export const menuOptionClickedEvent = (
  eventProperties: MenuOptionClickedEventProperties,
) => {
  const pathname = window.location.pathname;
  amplitude.track('Menu Option Clicked', {
    ...eventProperties,
    pathname,
  });
};

export const selectClientEvent = ({ customerName }: Customer) => {
  amplitude.track('Client Selected', { clientName: customerName });
};

type SelectModelEventProperties = {
  clientName: string;
  modelVersion: string;
  modelName: string;
  modelType: string;
};
export const selectModelEvent = (
  eventProperties: SelectModelEventProperties,
) => {
  amplitude.track('Model Selected', eventProperties);
};
